import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-azure-wafr-pillar-recommendations",
  templateUrl: "./azure-wafr-pillar-recommendations.component.html",
  styleUrls: [
    "../../wafr-shared.component.css",
    "./azure-wafr-pillar-recommendations.component.css",
  ],
})
export class AzureWafrPillarRecommendationsComponent implements OnInit {
  userId = localStorage.getItem("selectedClientList");
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  subscriptionId = "";
  selectedPillar = "";
  loading = false;
  errorMessage: string = null;
  rows: any[] = [];
  showInfoModal = false;
  selectedInfoRows: Array<{ field: string; value: string }> = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: APIService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.subscriptionId =
      this.route.snapshot.paramMap.get("subscriptionId") || "";
    this.selectedPillar = this.route.snapshot.queryParamMap.get("pillar") || "";
    this.load();
  }

  goBack() {
    this.router.navigate(
      ["/dash/azure/wafr", this.subscriptionId, "pillar-scores"],
      {
        queryParams: this.selectedPillar ? { pillar: this.selectedPillar } : {},
      }
    );
  }

  private normalizeImpact(value: any): string {
    return value ? `${value}` : "-";
  }

  private formatApiValue(value: any): string {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return `${value}`;
      }
    }
    return `${value}`;
  }

  private toTitleCaseLabel(key: string): string {
    if (!key) {
      return "-";
    }
    const spaced = key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim()
      .toLowerCase();
    return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private buildRecommendationDetails(
    recommendation: any
  ): Array<{ field: string; value: string }> {
    if (!recommendation || typeof recommendation !== "object") {
      return [];
    }

    return Object.keys(recommendation)
      .filter((key) => {
        const lowerKey = key.toLowerCase();
        return lowerKey !== "id" && lowerKey !== "name";
      })
      .map((key) => {
        return {
          field: this.toTitleCaseLabel(key),
          value: this.formatApiValue(recommendation[key]),
        };
      });
  }

  private getPillarsToShow(pillars: any[]): any[] {
    if (!this.selectedPillar) {
      return pillars || [];
    }
    return (pillars || []).filter((pillar: any) => {
      return (
        (pillar?.wafr_pillar || "").toLowerCase() ==
        this.selectedPillar.toLowerCase()
      );
    });
  }

  async load() {
    this.loading = true;
    this.errorMessage = null;
    this.rows = [];

    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/wafr/api/v1/wafr/pillar-recommendations` +
      `?user_id=${encodeURIComponent(this.userId)}` +
      `&subscription_id=${encodeURIComponent(this.subscriptionId)}`;

    try {
      const result: any = await this.apiService.getDataPromis(apiUrl, {}, headers);
      if (result && Array.isArray(result.pillars)) {
        const pillars = this.getPillarsToShow(result.pillars);
        this.rows = pillars
          .map((pillar: any) => {
            const recommendations = Array.isArray(pillar.recommendations)
              ? pillar.recommendations
              : [];
            return recommendations.map((recommendation: any) => {
              return {
                type: recommendation.type || "-",
                category:
                  recommendation.category ||
                  pillar.advisor_category ||
                  "-",
                impact: this.normalizeImpact(recommendation.impact),
                problem: recommendation.problem || "-",
                solution: recommendation.solution || "-",
                impacted_field: recommendation.impacted_field || "-",
                impacted_value: recommendation.impacted_value || "-",
                details: this.buildRecommendationDetails(recommendation),
              };
            });
          })
          .reduce((acc: any[], cur: any[]) => acc.concat(cur), []);
      } else {
        this.errorMessage =
          result?.error_message ||
          result?.message ||
          "Unable to load WAFR pillar recommendations.";
        this.notifier.alert("Error", "", this.errorMessage, "error", 5000);
      }
    } catch (err: any) {
      this.errorMessage =
        err?.toString() || "Unable to load WAFR pillar recommendations.";
    } finally {
      this.loading = false;
    }
  }

  openInfoModal(details: Array<{ field: string; value: string }>) {
    this.selectedInfoRows = Array.isArray(details) ? details : [];
    this.showInfoModal = true;
  }

  closeInfoModal() {
    this.showInfoModal = false;
    this.selectedInfoRows = [];
  }
}
